import React, { useState, useEffect, useCallback } from "react";
import {
  Rocket, ShieldCheck, DatabaseBackup, ListChecks, AlertTriangle, CheckCircle2,
  XCircle, Wallet, Lock, ChevronRight, ChevronLeft, RefreshCw,
} from "lucide-react";
import { Modal, Button, Input, Badge, Spinner } from "../lib/ui";
import { useNavigate } from "react-router-dom";
import { useToast } from "../lib/toast";
import api from "../lib/api";

const STEPS = ["Kesiapan", "Backup", "Pilih Data", "Konfirmasi", "Verifikasi", "Go-Live"];

const MASTER_DATA_KEPT = [
  "Cabang & Gudang", "Supplier", "Karyawan", "Menu & Resep", "Item Inventory",
  "Kategori & Satuan", "Chart of Accounts", "Pengaturan Pajak & Perusahaan",
  "Akun Pengguna", "Role & Permissions",
];

function StepDots({ step }) {
  return (
    <div className="mb-4 flex items-center gap-1 overflow-x-auto" data-testid="freshstart-steps">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${i === step ? "bg-primary text-white" : i < step ? "bg-success-light text-success-text" : "bg-stone-100 text-ink-mute"}`}>
            {i < step ? <CheckCircle2 size={12} /> : <span>{i + 1}</span>} {s}
          </div>
          {i < STEPS.length - 1 && <ChevronRight size={12} className="shrink-0 text-ink-mute" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function ReadinessBadge({ status }) {
  if (status === "pass") return <Badge tone="success"><CheckCircle2 size={12} /> LOLOS</Badge>;
  if (status === "warning") return <Badge tone="warn"><AlertTriangle size={12} /> PERINGATAN</Badge>;
  return <Badge tone="danger"><XCircle size={12} /> BLOKIR</Badge>;
}

// ---------------- Step 0: Readiness ----------------
function StepReadiness({ readiness, onRefresh, refreshing }) {
  return (
    <div className="space-y-3" data-testid="freshstart-readiness">
      <p className="text-sm text-ink-soft">
        Sebelum menyiapkan sistem untuk hari operasional pertama, kami memeriksa kesehatan pembukuan
        dan dokumen operasional yang masih menggantung.
      </p>
      <div className="space-y-2">
        {(readiness?.checks || []).map((c) => (
          <div key={c.key} className="flex items-start justify-between gap-3 rounded-xl border border-line px-3.5 py-2.5" data-testid={`freshstart-check-${c.key}`}>
            <div>
              <p className="text-sm font-semibold text-ink">{c.label}</p>
              <p className="text-xs text-ink-mute">{c.detail}</p>
              {c.status === "warning" && <p className="mt-0.5 text-[11px] text-warn-text">Akan ikut dibersihkan jika scope terkait dipilih pada langkah berikutnya.</p>}
            </div>
            <ReadinessBadge status={c.status} />
          </div>
        ))}
      </div>
      {readiness?.blockers > 0 && (
        <div className="flex items-start gap-2 rounded-xl bg-danger-light px-3.5 py-2.5 text-xs text-danger-text" data-testid="freshstart-blocker-note">
          <XCircle size={14} className="mt-0.5 shrink-0" />
          Ada {readiness.blockers} masalah kritis yang harus diselesaikan dulu (buku besar tidak seimbang / antrean jurnal).
          Fresh Start diblokir sampai semuanya LOLOS.
        </div>
      )}
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="freshstart-readiness-refresh">
        <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Periksa Ulang
      </Button>
    </div>
  );
}

// ---------------- Step 1: Backup ----------------
function StepBackup({ backup, onCreate, creating }) {
  return (
    <div className="space-y-3" data-testid="freshstart-backup">
      <p className="text-sm text-ink-soft">
        Sebelum data apa pun dibersihkan, seluruh isi sistem disalin ke backup pengaman.
        Wizard tidak dapat melanjutkan sebelum backup berhasil.
      </p>
      {backup ? (
        <div className={`rounded-xl border-2 p-4 ${backup.status === "completed" ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="freshstart-backup-info">
          <div className="mb-1 flex items-center gap-2">
            <DatabaseBackup size={16} className={backup.status === "completed" ? "text-emerald-600" : "text-amber-600"} />
            <p className="font-mono text-sm font-bold text-ink" data-testid="freshstart-backup-name">{backup.name}</p>
          </div>
          <p className="text-xs text-ink-soft">Dibuat: {(backup.created_at || "").slice(0, 19).replace("T", " ")} · oleh {backup.created_by}</p>
          <p className="text-xs text-ink-soft">Total data tersalin: <b>{Number(backup.total_docs || 0).toLocaleString("id-ID")}</b> dokumen</p>
          <div className="mt-1.5 flex gap-2">
            {backup.status === "completed"
              ? <Badge tone="success" data-testid="freshstart-backup-status"><CheckCircle2 size={12} /> BERHASIL</Badge>
              : <Badge tone="warn" data-testid="freshstart-backup-status">{backup.status}</Badge>}
            {backup.verification === "verified"
              ? <Badge tone="success" data-testid="freshstart-backup-verif"><ShieldCheck size={12} /> TERVERIFIKASI</Badge>
              : <Badge tone="danger" data-testid="freshstart-backup-verif">BELUM TERVERIFIKASI</Badge>}
          </div>
          {backup.baseline && <p className="mt-1.5 text-[11px] font-semibold text-amber-700">🏛 Baseline Produksi — permanen, tidak pernah dihapus otomatis.</p>}
        </div>
      ) : (
        <div className="rounded-xl bg-stone-50 px-4 py-3 text-sm text-ink-mute">Belum ada backup untuk sesi ini.</div>
      )}
      <Button onClick={onCreate} loading={creating} data-testid="freshstart-backup-create">
        <DatabaseBackup size={15} /> {backup ? "Buat Backup Baru" : "Buat Backup Sekarang"}
      </Button>
    </div>
  );
}

// ---------------- Step 2: Cleanup selection ----------------
function StepCleanup({ scopes, mandatory, selected, setSelected, salesMode, setSalesMode }) {
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const salesScope = scopes.find((s) => s.id === "sales");
  return (
    <div className="space-y-4" data-testid="freshstart-cleanup">
      <p className="text-sm text-ink-soft">
        Pilih data transaksi masa uji coba yang akan dihapus. <b>Master Data tidak pernah ikut terhapus.</b>
      </p>

      {/* Sales history option */}
      <div className="rounded-xl border border-line p-3.5" data-testid="freshstart-sales-option">
        <p className="mb-2 text-sm font-bold text-ink">Riwayat Penjualan {salesScope ? `(${salesScope.count.toLocaleString("id-ID")} data)` : ""}</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-soft">
            <input type="radio" name="salesMode" checked={salesMode === "reset"} onChange={() => setSalesMode("reset")} data-testid="freshstart-sales-reset" />
            Hapus riwayat penjualan
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-soft">
            <input type="radio" name="salesMode" checked={salesMode === "keep"} onChange={() => setSalesMode("keep")} data-testid="freshstart-sales-keep" />
            Pertahankan riwayat penjualan
          </label>
        </div>
      </div>

      {/* Scope checklist */}
      <div className="grid gap-2 sm:grid-cols-2">
        {scopes.filter((s) => s.id !== "sales").map((s) => (
          <label key={s.id} className={`flex cursor-pointer items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors ${selected[s.id] ? "border-primary bg-primary/5" : "border-line"}`} data-testid={`freshstart-scope-${s.id}`}>
            <span className="flex items-center gap-2">
              <input type="checkbox" checked={!!selected[s.id]} onChange={() => toggle(s.id)} />
              <span className="font-semibold text-ink">{s.label}</span>
            </span>
            <span className="shrink-0 text-xs text-ink-mute">{s.count.toLocaleString("id-ID")}</span>
          </label>
        ))}
        {mandatory.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 rounded-xl border border-line bg-stone-50 px-3 py-2.5 text-sm" data-testid={`freshstart-mandatory-${s.id}`}>
            <span className="flex items-center gap-2 text-ink-soft"><Lock size={13} /> <span className="font-semibold">{s.label}</span></span>
            <span className="shrink-0 text-xs text-ink-mute">{s.count.toLocaleString("id-ID")}</span>
          </div>
        ))}
      </div>

      {/* Master data preserved */}
      <div className="rounded-xl bg-success-light p-3.5" data-testid="freshstart-masterdata">
        <p className="mb-1.5 flex items-center gap-1.5 text-sm font-bold text-success-text"><ShieldCheck size={15} /> Selalu dipertahankan (Master Data)</p>
        <div className="flex flex-wrap gap-1.5">
          {MASTER_DATA_KEPT.map((m) => <span key={m} className="rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-success-text">{m}</span>)}
        </div>
      </div>
    </div>
  );
}

// ---------------- Step 3: Confirmation ----------------
function StepConfirm({ backup, scopeCount, confirm, setConfirm, result }) {
  if (result) {
    const total = Object.values(result.deleted || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
    return (
      <div className="space-y-3" data-testid="freshstart-execute-result">
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-center">
          <CheckCircle2 size={28} className="mx-auto mb-1 text-emerald-600" />
          <p className="font-heading text-lg font-bold text-emerald-700">Pembersihan Selesai</p>
          <p className="text-sm text-ink-soft">{total.toLocaleString("id-ID")} data transaksi dihapus · buku besar dibangun ulang otomatis.</p>
        </div>
        <p className="text-sm text-ink-soft">Lanjutkan untuk mengisi saldo awal usaha Anda.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="freshstart-confirm">
      <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
        <p className="mb-1.5 flex items-center gap-1.5 font-bold"><AlertTriangle size={16} /> Anda akan menyiapkan Central Flow untuk produksi nyata.</p>
        <ul className="list-disc space-y-0.5 pl-5">
          <li>Semua transaksi operasional yang dipilih ({scopeCount} kategori) akan <b>dihapus</b>.</li>
          <li>Master Data akan tetap utuh.</li>
          <li>Backup lengkap telah dibuat: <b className="font-mono">{backup?.name}</b>.</li>
          <li>Tindakan ini hanya boleh dilakukan sebelum hari operasional pertama.</li>
        </ul>
      </div>
      <Input label='Ketik "GO LIVE" untuk konfirmasi' value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="GO LIVE" data-testid="freshstart-confirm-input" />
    </div>
  );
}

// ---------------- Step 4: Go-Live Verification ----------------
function StepVerify({ verification, zeroStart, setZeroStart, onNavigate, onRefresh, refreshing }) {
  const v = verification || {};
  const gate = v.can_go_live || zeroStart;
  return (
    <div className="space-y-3" data-testid="freshstart-verify">
      <p className="text-sm text-ink-soft">
        Sebelum Production Mode diaktifkan, sistem memverifikasi bahwa <b>Beginning Balance</b> dan
        <b> First Opening Stock Take</b> telah diselesaikan setelah pembersihan.
      </p>

      {/* Beginning Balance */}
      <div className="flex items-start justify-between gap-3 rounded-xl border border-line px-3.5 py-2.5" data-testid="freshstart-verify-bb">
        <div>
          <p className="text-sm font-semibold text-ink">Beginning Balance (saldo keuangan awal)</p>
          <p className="text-xs text-ink-mute">
            {v.beginning_balance_done
              ? `Ditandai selesai · ${v.beginning_balance_entities_saved} lokasi tersimpan`
              : "Belum ditandai selesai — isi di menu Beginning Balance lalu klik \u201CTandai Selesai\u201D."}
          </p>
          {!v.beginning_balance_done && (
            <button onClick={() => onNavigate("/beginning-balance")} className="mt-1 text-xs font-bold text-primary underline" data-testid="freshstart-goto-bb">
              Buka Beginning Balance →
            </button>
          )}
        </div>
        {v.beginning_balance_done
          ? <Badge tone="success"><CheckCircle2 size={12} /> SELESAI</Badge>
          : <Badge tone="warn"><AlertTriangle size={12} /> BELUM</Badge>}
      </div>

      {/* Opening Stock Take */}
      <div className="rounded-xl border border-line px-3.5 py-2.5" data-testid="freshstart-verify-stock">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-ink">First Opening Stock Take (stok fisik awal)</p>
            <p className="text-xs text-ink-mute">
              {v.opening_stock_done
                ? `${v.opening_stock_sessions} sesi stok awal difinalisasi`
                : "Belum ada stok awal yang difinalisasi — jalankan lewat menu Stock Take → Stok Awal (First Opening)."}
            </p>
            {!v.opening_stock_done && (
              <button onClick={() => onNavigate("/opname")} className="mt-1 text-xs font-bold text-primary underline" data-testid="freshstart-goto-opname">
                Buka Stock Take →
              </button>
            )}
          </div>
          {v.opening_stock_done
            ? <Badge tone="success"><CheckCircle2 size={12} /> SELESAI</Badge>
            : <Badge tone="warn"><AlertTriangle size={12} /> BELUM</Badge>}
        </div>
        {(v.entities || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {v.entities.map((e) => (
              <span key={e.id} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${e.opening_stock_done ? "bg-success-light text-success-text" : "bg-stone-100 text-ink-mute"}`} data-testid={`freshstart-verify-entity-${e.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {e.opening_stock_done ? "✓" : "○"} {e.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Zero-start exception */}
      {!v.can_go_live && (
        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border-2 border-amber-300 bg-amber-50 px-3.5 py-3 text-sm text-amber-900" data-testid="freshstart-zero-start">
          <input type="checkbox" checked={zeroStart} onChange={(e) => setZeroStart(e.target.checked)} className="mt-0.5" data-testid="freshstart-zero-start-checkbox" />
          <span>
            <b>Bisnis ini benar-benar dimulai dari NOL.</b> Saya (Owner) mengonfirmasi tidak ada saldo
            keuangan awal maupun stok fisik awal yang perlu dicatat. Konfirmasi ini direkam permanen
            pada Catatan Aktivasi Produksi.
          </span>
        </label>
      )}

      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="freshstart-verify-refresh">
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Periksa Ulang
        </Button>
        {gate
          ? <Badge tone="success" data-testid="freshstart-verify-pass"><CheckCircle2 size={12} /> Verifikasi terpenuhi</Badge>
          : <Badge tone="danger" data-testid="freshstart-verify-blocked"><XCircle size={12} /> Go-Live diblokir</Badge>}
      </div>
    </div>
  );
}

// ---------------- Step 5: Go Live ----------------
function StepGoLive({ backup, firstDate, setFirstDate, confirm, setConfirm, goLive }) {
  if (goLive) {
    return (
      <div className="space-y-3" data-testid="freshstart-golive-record">
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-center">
          <Rocket size={28} className="mx-auto mb-1 text-emerald-600" />
          <p className="font-heading text-lg font-bold text-emerald-700">PRODUCTION MODE AKTIF</p>
          <p className="text-sm text-ink-soft">Central Flow resmi berjalan sebagai sistem produksi.</p>
        </div>
        <div className="rounded-xl border border-line p-4 text-sm">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink-mute">Catatan Aktivasi Produksi (Permanen)</p>
          <div className="space-y-1">
            <div className="flex justify-between"><span className="text-ink-soft">Tanggal Go-Live</span><b data-testid="golive-date">{goLive.go_live_date}</b></div>
            <div className="flex justify-between"><span className="text-ink-soft">Operasional Pertama</span><b>{goLive.first_operational_date}</b></div>
            <div className="flex justify-between"><span className="text-ink-soft">Dieksekusi oleh</span><b>{goLive.executed_by}</b></div>
            <div className="flex justify-between"><span className="text-ink-soft">Referensi Backup</span><b className="font-mono text-xs">{goLive.backup_reference}</b></div>
            <div className="flex justify-between"><span className="text-ink-soft">Waktu Aktivasi</span><b>{(goLive.activated_at || "").slice(0, 19).replace("T", " ")}</b></div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="freshstart-golive">
      <p className="text-sm text-ink-soft">
        Langkah terakhir: sistem beralih dari <b>Implementation Mode</b> ke <b>Production Mode</b>.
        Setelah aktif, Hard Delete, System Reset, dan Fresh Start dinonaktifkan — hanya
        Reverse Transaction & Force Correction yang tersedia untuk koreksi.
      </p>
      <Input label="Tanggal operasional pertama" type="date" value={firstDate} onChange={(e) => setFirstDate(e.target.value)} data-testid="freshstart-golive-date" />
      <div className="rounded-xl bg-stone-50 px-3.5 py-2.5 text-xs text-ink-soft">
        Referensi backup: <b className="font-mono">{backup?.name || "—"}</b>
      </div>
      <Input label='Ketik "GO LIVE" untuk mengaktifkan Production Mode' value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="GO LIVE" data-testid="freshstart-golive-confirm" />
    </div>
  );
}

// ================= Main wizard =================
export default function FreshStartWizard({ open, onClose, onDone }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [backup, setBackup] = useState(null);
  const [selected, setSelected] = useState({});
  const [salesMode, setSalesMode] = useState("reset");
  const [confirm, setConfirm] = useState("");
  const [execResult, setExecResult] = useState(null);
  const [zeroStart, setZeroStart] = useState(false);
  const [firstDate, setFirstDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [goLiveConfirm, setGoLiveConfirm] = useState("");
  const [goLive, setGoLive] = useState(null);

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrity/fresh-start/state");
      setState(data);
      if (data.last_backup?.status === "completed" && data.last_backup?.verification === "verified" && data.last_backup?.type === "fresh_start") setBackup(data.last_backup);
      const sel = {};
      (data.scopes || []).forEach((s) => { if (s.id !== "sales") sel[s.id] = true; });
      setSelected((prev) => (Object.keys(prev).length ? prev : sel));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat status Fresh Start"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { if (open) loadState(); }, [open, loadState]);

  const refreshReadiness = async () => {
    setBusy(true);
    try { const { data } = await api.get("/integrity/fresh-start/readiness"); setState((s) => ({ ...s, readiness: data })); }
    catch { toast.error("Gagal memeriksa kesiapan"); } finally { setBusy(false); }
  };

  const createBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrity/fresh-start/backup");
      setBackup(data);
      toast.success("Backup pengaman berhasil dibuat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Backup gagal"); }
    finally { setBusy(false); }
  };

  const execute = async () => {
    const scopes = Object.keys(selected).filter((k) => selected[k]);
    if (salesMode === "reset") scopes.push("sales");
    setBusy(true);
    try {
      const { data } = await api.post("/integrity/fresh-start/execute", { scopes, backup_id: backup.id, confirm });
      setExecResult(data);
      toast.success("Pembersihan selesai — buku besar dibangun ulang");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eksekusi gagal"); }
    finally { setBusy(false); }
  };

  const saveOpeningRefresh = async () => {
    setBusy(true);
    try { await loadState(); } finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrity/fresh-start/complete",
        { confirm: goLiveConfirm, first_operational_date: firstDate, zero_start: zeroStart });
      setGoLive(data.go_live);
      toast.success("Production Mode aktif — selamat Go-Live! 🎉");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengaktifkan Production Mode"); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  const readiness = state?.readiness;
  const verification = state?.golive_verification;
  const scopeCount = Object.values(selected).filter(Boolean).length + (salesMode === "reset" ? 1 : 0);

  const canNext = () => {
    if (step === 0) return readiness?.can_proceed;
    if (step === 1) return backup?.status === "completed" && backup?.verification === "verified";
    if (step === 2) return scopeCount > 0;
    if (step === 4) return verification?.can_go_live || zeroStart;
    return true;
  };

  const footer = (() => {
    if (loading) return null;
    const back = step > 0 && step !== 3 && !goLive
      ? <Button variant="outline" onClick={() => setStep(step - 1)} disabled={busy} data-testid="freshstart-back"><ChevronLeft size={15} /> Kembali</Button>
      : <Button variant="outline" onClick={onClose} disabled={busy}>Tutup</Button>;
    let action = null;
    if (step < 3) {
      action = <Button onClick={() => setStep(step + 1)} disabled={!canNext() || busy} data-testid="freshstart-next">Lanjut <ChevronRight size={15} /></Button>;
    } else if (step === 3) {
      action = execResult
        ? <Button onClick={async () => { await saveOpeningRefresh(); setStep(4); }} data-testid="freshstart-next">Lanjut <ChevronRight size={15} /></Button>
        : <Button variant="danger" onClick={execute} loading={busy} disabled={confirm !== "GO LIVE"} data-testid="freshstart-execute">Jalankan Pembersihan</Button>;
    } else if (step === 4) {
      action = <Button onClick={() => setStep(5)} disabled={!canNext() || busy} data-testid="freshstart-next">Lanjut <ChevronRight size={15} /></Button>;
    } else if (step === 5) {
      action = goLive
        ? <Button variant="success" onClick={() => { onDone?.(); onClose(); }} data-testid="freshstart-finish"><CheckCircle2 size={15} /> Selesai</Button>
        : <Button variant="success" onClick={complete} loading={busy} disabled={goLiveConfirm !== "GO LIVE"} data-testid="freshstart-activate"><Rocket size={15} /> Aktifkan Production Mode</Button>;
    }
    return <>{back}{action}</>;
  })();

  return (
    <Modal open onClose={busy ? () => {} : onClose} title="🚀 Fresh Start Wizard — Persiapan Go-Live" size="lg" footer={footer}>
      <div data-testid="freshstart-wizard">
        <StepDots step={step} />
        {loading ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
          <>
            {step === 0 && <StepReadiness readiness={readiness} onRefresh={refreshReadiness} refreshing={busy} />}
            {step === 1 && <StepBackup backup={backup} onCreate={createBackup} creating={busy} />}
            {step === 2 && <StepCleanup scopes={state?.scopes || []} mandatory={state?.mandatory_scopes || []} selected={selected} setSelected={setSelected} salesMode={salesMode} setSalesMode={setSalesMode} />}
            {step === 3 && <StepConfirm backup={backup} scopeCount={scopeCount} confirm={confirm} setConfirm={setConfirm} result={execResult} />}
            {step === 4 && <StepVerify verification={verification} zeroStart={zeroStart} setZeroStart={setZeroStart}
              onNavigate={(to) => { onClose(); navigate(to); }} onRefresh={saveOpeningRefresh} refreshing={busy} />}
            {step === 5 && <StepGoLive backup={backup} firstDate={firstDate} setFirstDate={setFirstDate} confirm={goLiveConfirm} setConfirm={setGoLiveConfirm} goLive={goLive} />}
          </>
        )}
      </div>
    </Modal>
  );
}
